import { useState } from 'react'
import { router } from '@inertiajs/react'
import { useFlashToast } from '../../../hooks/useFlashToast'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faFileLines, faMagnifyingGlass, faCircleDot, faHourglassHalf,
  faCircleCheck, faCircleXmark, faChevronRight, faCalendar,
  faLocationDot, faEyeSlash, faFilter, faBuilding, faGlobe,
  faInbox, faFunnelDollar, faPaperclip, faDownload
} from '@fortawesome/free-solid-svg-icons'
import AdminSidebar from '../../../components/admin/Sidebar'
import AdminTopbar from '../../../components/admin/Topbar'

const STATUS_CONFIG = {
  menunggu: { label: 'Menunggu',        icon: faCircleDot,     bg: 'bg-gray-100',    text: 'text-gray-600',    ring: 'ring-gray-300',   bar: 'bg-gray-400' },
  diproses: { label: 'Sedang Diproses', icon: faHourglassHalf, bg: 'bg-amber-50',    text: 'text-amber-700',   ring: 'ring-amber-200',  bar: 'bg-amber-400' },
  selesai:  { label: 'Selesai',         icon: faCircleCheck,   bg: 'bg-emerald-50',  text: 'text-emerald-700', ring: 'ring-emerald-200', bar: 'bg-emerald-500' },
  ditolak:  { label: 'Ditolak',         icon: faCircleXmark,   bg: 'bg-red-50',      text: 'text-red-600',     ring: 'ring-red-200',    bar: 'bg-red-400' },
}

const CATEGORY_EMOJI = {
  'Korupsi & Gratifikasi': '🏛️', 'Penyalahgunaan Wewenang': '⚖️',
  'Konflik Kepentingan': '🔄', 'Penggelapan Aset Negara': '📦',
  'Suap & Pungli': '💰', 'Pemalsuan Dokumen': '📄',
  'Pelanggaran Kode Etik': '📋', 'Kecurangan Pengadaan': '🛒',
}

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.menunggu
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ring-1 ${cfg.bg} ${cfg.text} ${cfg.ring}`}>
      <FontAwesomeIcon icon={cfg.icon} className="w-3 h-3" />
      {cfg.label}
    </span>
  )
}

export default function AdminReportsIndex({ current_user, reports, status_filter, query, stats }) {
  useFlashToast()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [searchVal, setSearchVal] = useState(query)

  const navigate = (newStatus, q) => {
    router.get('/admin/reports', { status: newStatus, q: q ?? searchVal }, { preserveState: true })
  }

  const handleSearch = (e) => {
    e.preventDefault()
    navigate(status_filter, searchVal)
  }

  const TABS = [
    { key: '',         label: 'Semua',   value: stats.total    },
    { key: 'menunggu', label: 'Menunggu', value: stats.menunggu },
    { key: 'diproses', label: 'Diproses', value: stats.diproses },
    { key: 'selesai',  label: 'Selesai',  value: stats.selesai  },
    { key: 'ditolak',  label: 'Ditolak',  value: stats.ditolak  },
  ]

  return (
    <div className="flex h-screen bg-gray-50 font-sans overflow-hidden">
      <AdminSidebar current_user={current_user} open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <AdminTopbar current_user={current_user} onMenuToggle={() => setSidebarOpen(true)}>
          <form onSubmit={handleSearch} className="flex-1 max-w-md">
            <div className="relative">
              <FontAwesomeIcon icon={faMagnifyingGlass} className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input value={searchVal} onChange={e => setSearchVal(e.target.value)}
                placeholder="Cari judul atau kode pelacakan..."
                className="w-full pl-9 pr-4 py-2 text-sm bg-white border border-gray-200 text-gray-700 placeholder-gray-400 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none shadow-sm" />
            </div>
          </form>
        </AdminTopbar>

        <main className="flex-1 overflow-y-auto p-4 lg:p-6">

          <div className="mb-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div>
              <h1 className="text-xl font-bold text-gray-900">Manajemen Laporan</h1>
              <p className="text-sm text-gray-500 mt-0.5">Kelola dan tindak lanjuti laporan dari masyarakat</p>
            </div>
            <a
              href={`/admin/reports/export?status=${status_filter}&q=${encodeURIComponent(query || '')}`}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold bg-white border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 shadow-sm"
            >
              <FontAwesomeIcon icon={faDownload} className="w-3.5 h-3.5" />
              Export CSV
            </a>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
            {[
              { label: 'Total',    value: stats.total,    color: 'blue',    icon: faFileLines },
              { label: 'Menunggu', value: stats.menunggu, color: 'gray',    icon: faCircleDot },
              { label: 'Diproses', value: stats.diproses, color: 'amber',   icon: faHourglassHalf },
              { label: 'Selesai',  value: stats.selesai,  color: 'emerald', icon: faCircleCheck },
              { label: 'Ditolak',  value: stats.ditolak,  color: 'red',     icon: faCircleXmark },
            ].map(s => {
              const colorMap = {
                blue:    { bg: 'bg-blue-50',    text: 'text-blue-600',    icon: 'bg-blue-100' },
                gray:    { bg: 'bg-gray-100',   text: 'text-gray-600',    icon: 'bg-gray-200' },
                amber:   { bg: 'bg-amber-50',   text: 'text-amber-600',   icon: 'bg-amber-100' },
                emerald: { bg: 'bg-emerald-50', text: 'text-emerald-600', icon: 'bg-emerald-100' },
                red:     { bg: 'bg-red-50',     text: 'text-red-600',     icon: 'bg-red-100' },
              }[s.color]
              return (
                <div key={s.label} className={`${colorMap.bg} rounded-xl border border-transparent p-4 flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity`}
                  onClick={() => navigate(s.label === 'Total' ? '' : s.label.toLowerCase())}>
                  <div className={`w-9 h-9 ${colorMap.icon} rounded-xl flex items-center justify-center flex-shrink-0`}>
                    <FontAwesomeIcon icon={s.icon} className={`w-4 h-4 ${colorMap.text}`} />
                  </div>
                  <div>
                    <div className={`text-xl font-bold ${colorMap.text}`}>{s.value}</div>
                    <div className="text-[11px] text-gray-500">{s.label}</div>
                  </div>
                </div>
              )
            })}
          </div>

          {/* Tabs */}
          <div className="flex flex-wrap gap-1 mb-4 bg-white border border-gray-200 rounded-xl p-1 w-fit shadow-sm">
            {TABS.map(t => (
              <button key={t.key} onClick={() => navigate(t.key)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  status_filter === t.key
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'
                }`}>
                {t.label}
                <span className={`text-[11px] font-bold px-1.5 py-0.5 rounded-full ${
                  status_filter === t.key ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-500'
                }`}>{t.value}</span>
              </button>
            ))}
          </div>

          {/* Report list */}
          <div className="space-y-3">
            {reports.length === 0 && (
              <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
                <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <FontAwesomeIcon icon={faInbox} className="w-5 h-5 text-gray-400" />
                </div>
                <p className="text-gray-500 text-sm">Tidak ada laporan ditemukan</p>
              </div>
            )}
            {reports.map(report => {
              const cfg = STATUS_CONFIG[report.status] || STATUS_CONFIG.menunggu
              return (
                <a key={report.id} href={`/admin/reports/${report.id}`}
                  className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md hover:border-blue-200 transition-all block p-4 lg:p-5">
                  <div className="flex items-start gap-4">

                    {/* Left accent */}
                    <div className={`w-1 h-16 rounded-full flex-shrink-0 ${cfg.bar}`} />

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <span className="text-[11px] font-mono text-gray-400">{report.tracking_code}</span>
                            <span className="text-gray-300">·</span>
                            <span className="text-xs text-gray-500">{CATEGORY_EMOJI[report.category]} {report.category}</span>
                            {report.anonymous && (
                              <span className="flex items-center gap-1 text-[11px] text-gray-400">
                                <FontAwesomeIcon icon={faEyeSlash} className="w-3 h-3" /> Anonim
                              </span>
                            )}
                            {report.has_attachments && (
                              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-violet-100 text-violet-600 ring-1 ring-violet-200">
                                <FontAwesomeIcon icon={faPaperclip} className="w-2.5 h-2.5" />
                                {report.attachments_count} Lampiran
                              </span>
                            )}
                          </div>
                          <h3 className="text-sm font-semibold text-gray-900 line-clamp-2">{report.title}</h3>
                          <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                            {report.location && (
                              <span className="flex items-center gap-1 text-[11px] text-gray-400">
                                <FontAwesomeIcon icon={faLocationDot} className="w-3 h-3" /> {report.location}
                              </span>
                            )}
                            <span className="flex items-center gap-1 text-[11px] text-gray-400">
                              <FontAwesomeIcon icon={faCalendar} className="w-3 h-3" /> {report.created_at}
                            </span>
                          </div>
                          {!report.anonymous && report.reporter && (
                            <div className="mt-1.5 flex items-center gap-1 text-[11px] text-gray-400">
                              <FontAwesomeIcon icon={report.reporter.type === 'internal' ? faBuilding : faGlobe} className="w-3 h-3" />
                              <span>{report.reporter.name}</span>
                              <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium ${report.reporter.type === 'internal' ? 'bg-violet-100 text-violet-600' : 'bg-cyan-100 text-cyan-600'}`}>
                                {report.reporter.type}
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="flex flex-col items-end gap-2 flex-shrink-0">
                          <StatusBadge status={report.status} />
                        </div>
                      </div>
                    </div>

                    <FontAwesomeIcon icon={faChevronRight} className="w-3.5 h-3.5 text-gray-300 flex-shrink-0 mt-1" />
                  </div>
                </a>
              )
            })}
          </div>
        </main>
      </div>
    </div>
  )
}
